import { Close as CancelIcon } from '@mui/icons-material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Button, Card, Grid, InputLabel, Tooltip } from '@mui/material';
import React, { Dispatch, FC, SetStateAction, useRef } from 'react';

import { SnackBarProps } from '../../types';

const cleanFileName = (name: string = ''): string => name.replace(/[^a-zA-Z0-9\s.]/g, '').replace(/\s/g, '_');
type Attachment = {
	file: File;
	filePath: string;
};

const readAsDataURL = (file: File) =>
	new Promise<string>((resolve, reject) => {
		const fr = new FileReader();
		fr.onerror = () => reject(fr.error);
		fr.onload = () => resolve(String(fr.result));
		fr.readAsDataURL(file);
	});

export const handleAddFile = async (
	event: React.ChangeEvent<HTMLInputElement>,
	setShowSnackbar: React.Dispatch<React.SetStateAction<Partial<SnackBarProps> | null>>,
	setAttachment: React.Dispatch<React.SetStateAction<Attachment | null>>,
) => {
	const file = event.target.files?.[0] ?? null;
	if (!file) return;

	const sanitized = new File([file], cleanFileName(file.name), { type: file.type });

	const filePath = await readAsDataURL(sanitized);

	setAttachment({ file: sanitized, filePath });
	event.target.value = '';
};

export const handleRemoveFile = (setAttachment: Dispatch<SetStateAction<Attachment | null>>) => {
	setAttachment(null);
};

interface Props {
	attachment: Attachment | null;
	setShowSnackbar: Dispatch<SetStateAction<Partial<SnackBarProps> | null>>;
	setAttachment: Dispatch<SetStateAction<Attachment | null>>;
	required: boolean;
}

export const FileSelection: FC<Props> = ({ attachment, setShowSnackbar, setAttachment, required = false }) => {
	const fileRef = useRef<HTMLInputElement | null>(null);
	const MAX_FILE_NAME_LENGTH = 17;

	return (
		<Grid container spacing={4}>
			<Grid xs={12} item>
				<InputLabel>
					Attachment{required ? '*' : ''}
					<Button
						onClick={() => fileRef.current?.click()}
						data-cy='attachment-browse-button'
						sx={{ ml: 1.25 }}
					>
						<Tooltip title='Accepts .jpeg, .png, .pdf, and .csv (max 256 KB)' placement='top' arrow>
							<span>Browse</span>
						</Tooltip>
						<input
							ref={fileRef}
							type='file'
							multiple={false}
							accept='image/*,.pdf,.csv'
							style={{ display: 'none' }}
							data-cy='attachment-upload-input'
							onChange={(e) => handleAddFile(e, setShowSnackbar, setAttachment)}
						/>
					</Button>
				</InputLabel>

				{attachment && (
					<Grid container spacing={4} sx={{ mt: 0.5 }}>
						<Grid xs={12} sm={6} md={4} lg={3} item>
							<Card
								sx={{
									display: 'flex',
									alignItems: 'center',
									flexDirection: 'column',
									p: 1.25,
								}}
								data-cy='attachment-card'
							>
								<div
									style={{
										display: 'flex',
										width: '100%',
										justifyContent: 'flex-end',
									}}
								>
									<CancelIcon
										onClick={() => handleRemoveFile(setAttachment)}
										sx={{ cursor: 'pointer' }}
									/>
								</div>
								<InsertDriveFileIcon />
								<span style={{ color: 'black', marginTop: 10 }}>
									{attachment?.file.name.length > MAX_FILE_NAME_LENGTH
										? `${attachment?.file.name.substring(0, MAX_FILE_NAME_LENGTH)}...`
										: attachment?.file.name}
								</span>
							</Card>
						</Grid>
					</Grid>
				)}
			</Grid>
		</Grid>
	);
};
